import { useFigureCaptionForElement } from '@alpod-ai-writer/plate-plugins/figure-caption/react';
import { Image, useMediaState } from '@alpod-ai-writer/plate-plugins/media/react';
import { useSourceCaptionForElement } from '@alpod-ai-writer/plate-plugins/source-caption/react';
import { cn, withRef } from '@udecode/cn';
import { PlateElement, useRemoveNodeButton, withHOC } from '@udecode/plate/react';
import { useDraggable } from '@udecode/plate-dnd';
import { ResizableProvider } from '@udecode/plate-resizable';
import clsx from 'clsx';
import { LinkIcon, Link2OffIcon, PencilLineIcon, PencilOffIcon, TrashIcon } from 'lucide-react';

import {
    mediaResizeHandleVariants,
    Resizable,
    ResizeHandle,
} from '../common/resizable.component';
import { ToolbarButton } from '../toolbar/toolbarButton.component';
import { ToolbarSeparator } from '../toolbar/toolbarSeparator.component';

export const ImageElement = withHOC(
    ResizableProvider,
    withRef<typeof PlateElement>(
        ({ className, nodeProps, children, ...props }, ref) => {
            const { align = 'left', focused, readOnly, selected } = useMediaState();

            const { isDragging, handleRef } = useDraggable({
                element: props.element,
            });

            const { hasCaption, insertCaption, removeCaption } = useFigureCaptionForElement(props.element);
            const { hasSource, insertSource, removeSource } = useSourceCaptionForElement(props.element);
            const { props: removeButtonProps } = useRemoveNodeButton({ element: props.element });

            return (
                <PlateElement
                    ref={ref}
                    className={cn(className, 'py-2.5 min-h-8')}
                    {...props}
                >
                    <figure className="group relative m-0" contentEditable={false}>
                        <Resizable
                            align={align}
                            options={{
                                align,
                                readOnly,
                            }}
                        >
                            <ResizeHandle
                                className={mediaResizeHandleVariants({ direction: 'left' })}
                                options={{ direction: 'left' }}
                            />
                            <Image
                                ref={handleRef}
                                className={cn(
                                    'block w-full max-w-full cursor-pointer object-cover px-0',
                                    'rounded-sm',
                                    focused && selected && 'ring-2 ring-ring ring-offset-2',
                                    isDragging && 'opacity-50'
                                )}
                                alt=""
                                src={props.element ? `/storage/${props.element.url}` : undefined}
                                {...nodeProps}
                            />
                            <ResizeHandle
                                className={mediaResizeHandleVariants({
                                    direction: 'right',
                                })}
                                options={{ direction: 'right' }}
                            />
                        </Resizable>
                        {!readOnly && (
                            <div className={clsx(
                                'flex flex-row items-center gap-0.5 p-1 rounded-md bg-gray-200 shadow-[0px_3px_8px_rgba(85,96,117,0.35)]',
                                'opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-40',
                                align === 'left' && 'absolute top-2 left-2',
                                align === 'center' && 'absolute top-2 left-1/2 -translate-x-1/2',
                                align === 'right' && 'absolute top-2 right-2'
                            )}>
                                {!hasCaption && (
                                    <ToolbarButton size='sm' onClick={() => insertCaption('image')}><PencilLineIcon size={16} /> Vložit popisek</ToolbarButton>
                                )}
                                {hasCaption && (
                                    <ToolbarButton size='sm' onClick={removeCaption}><PencilOffIcon size={16} /> Odebrat popisek</ToolbarButton>
                                )}
                                {!hasSource && (
                                    <ToolbarButton size='sm' onClick={insertSource}><LinkIcon size={16} /> Vložit zdroj</ToolbarButton>
                                )}
                                {hasSource && (
                                    <ToolbarButton size='sm' onClick={removeSource}><Link2OffIcon size={16} /> Odebrat zdroj</ToolbarButton>
                                )}
                                <ToolbarSeparator />
                                <ToolbarButton size='sm' iconOnly {...removeButtonProps}><TrashIcon size={16} /></ToolbarButton>
                            </div>
                        )}
                        {children}
                    </figure>
                </PlateElement>
            );
        }
    )
);
